import { useState, useMemo } from 'react';
import { useGetFullServiceInvoicesQuery, useCreateFullServiceInvoiceMutation, useUpdateFullServiceInvoiceMutation, useDeleteFullServiceInvoiceMutation, useSendFullServiceToSapMutation } from '../../../api';
import { toastService } from '../../../utils/toastService';

const useFullServiceInvoice = () => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null
    });

    const [activeFilters, setActiveFilters] = useState({
        chassisNo: '', // string
        dealerId: undefined, // number | undefined
        contractStartDate: undefined, // Date | undefined
        contractEndDate: undefined // Date | undefined
    });

    const { data, isLoading, refetch } = useGetFullServiceInvoicesQuery({
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows,
        chassisNo: activeFilters.chassisNo || undefined,
        dealerId: typeof activeFilters.dealerId === 'number' ? activeFilters.dealerId : undefined,
        contractStartDate: activeFilters.contractStartDate,
        contractEndDate: activeFilters.contractEndDate
    });

    const [createFullServiceInvoice] = useCreateFullServiceInvoiceMutation();
    const [updateFullServiceInvoice] = useUpdateFullServiceInvoiceMutation();
    const [deleteFullServiceInvoice] = useDeleteFullServiceInvoiceMutation();
    const [sendToSap] = useSendFullServiceToSapMutation();

    const sortedData = useMemo(() => {
        const items = data?.data?.items || [];
        if (!lazyParams.sortField) return items;

        return [...items].sort((a, b) => {
            const aVal = a[lazyParams.sortField] || '';
            const bVal = b[lazyParams.sortField] || '';
            const order = lazyParams.sortOrder === 1 ? 1 : -1;
            return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
        });
    }, [data, lazyParams.sortField, lazyParams.sortOrder]);

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleFilterChange = (updatedFilters) => {
        setActiveFilters({
            chassisNo: updatedFilters.chassisNo || '',
            dealerId: updatedFilters.dealerId ? Number(updatedFilters.dealerId) : undefined,
            contractStartDate: updatedFilters.contractStartDate || undefined,
            contractEndDate: updatedFilters.contractEndDate || undefined
        });

        setLazyParams((prevParams) => ({
            ...prevParams,
            first: 0,
            page: 1,
            sortField: null,
            sortOrder: null
        }));
    };

    const initFilters = () => {
        setActiveFilters({
            chassisNo: '',
            dealerId: undefined,
            contractStartDate: undefined,
            contractEndDate: undefined
        });

        setLazyParams((prevParams) => ({
            ...prevParams,
            first: 0,
            page: 1,
            sortField: null,
            sortOrder: null
        }));
    };

    const handleCreate = async (formData) => {
        try {
            const response = await createFullServiceInvoice(formData).unwrap();

            const msg = response?.errorMessage || response?.ErrorMessage;
            if (typeof msg === 'string' && msg.trim().length > 0) {
                toastService.warn(msg, 'Uyarı', 6000);
            }
            refetch();
            return response;
        } catch (error) {
            console.error('Error creating invoice:', error);
            return false;
        }
    };

    const handleUpdate = async (formData) => {
        try {
            const response = await updateFullServiceInvoice(formData).unwrap();
            const msg = response?.errorMessage || response?.ErrorMessage;
            if (typeof msg === 'string' && msg.trim().length > 0) {
                toastService.warn(msg, 'Uyarı', 6000);
            }
            refetch();
            return response;
        } catch (error) {
            console.error('Error updating invoice:', error);
            return false;
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await deleteFullServiceInvoice(id).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error deleting invoice:', error);
            return false;
        }
    };

    const handleSendToSap = async (fullServiceId) => {
        try {
            const response = await sendToSap(fullServiceId).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error sending to SAP:', error);
            return false;
        }
    };

    return {
        invoices: sortedData,
        originalData: data?.data?.items || [],
        totalCount: data?.data?.totalCount || 0,
        loading: isLoading,
        lazyParams,
        onPage,
        onSort,
        handleCreate,
        handleUpdate,
        handleDelete,
        handleSendToSap,
        refetch,
        activeFilters,
        handleFilterChange,
        initFilters
    };
};

export default useFullServiceInvoice;
